import React, { useEffect } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { Box, Container, Typography, Grid, Card, CardActionArea, CardContent, Chip, Button } from '@mui/material';
import { LocationOn, Schedule } from '@mui/icons-material';
import PageLayout from '../components/PageLayout';
import Seo from '../components/Seo';
import serviceAreas from '../data/serviceAreas.json';
import { SITE } from '../config/siteInfo';
import { getBookingFlowPath } from '../utils/featureFlags';
import { trackButtonClick } from '../utils/analytics';

// Region display order for grouping the hub.
const REGION_ORDER = [
  'Central Florida',
  'Tampa Bay',
  'Sarasota & Manatee',
  'Southwest Florida',
  'Treasure Coast & SE Florida',
];

const buildHubJsonLd = () => ({
  '@context': 'https://schema.org',
  '@type': 'ItemList',
  name: 'House Cleaning Service Areas — 5 Fairies Cleaning',
  itemListElement: serviceAreas.cities.map((c, i) => ({
    '@type': 'ListItem',
    position: i + 1,
    name: `${c.city}, FL`,
    url: `${SITE.url}/house-cleaning/${c.slug}`,
  })),
});

const ServiceAreasHub = () => {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const regions = REGION_ORDER.map((region) => ({
    region,
    cities: serviceAreas.cities.filter((c) => c.region === region),
  })).filter((g) => g.cities.length > 0);

  return (
    <PageLayout>
      <Seo
        title="House Cleaning Service Areas Across Florida"
        description="See where 5 Fairies Cleaning provides house cleaning and maid service across Florida — Orlando, Tampa, Sarasota, Naples, Fort Myers, the Palm Beaches & more."
        path="/service-areas"
        jsonLd={buildHubJsonLd()}
      />

      <Box
        component="section"
        sx={{ background: 'linear-gradient(135deg, #000000 0%, #FFD700 100%)', color: 'white', py: { xs: 6, md: 9 } }}
      >
        <Container maxWidth="lg" sx={{ textAlign: 'center' }}>
          <Typography component="h1" variant="h3" sx={{ fontWeight: 800, mb: 2, fontSize: { xs: '2rem', md: '3rem' } }}>
            House Cleaning Service Areas Across Florida
          </Typography>
          <Typography variant="body1" sx={{ maxWidth: 780, mx: 'auto', fontSize: { xs: '1rem', md: '1.125rem' }, lineHeight: 1.7 }}>
            5 Fairies Cleaning provides insured, background-checked house cleaning and maid service across Central
            Florida, Tampa Bay, the Suncoast, Southwest Florida, and the Treasure Coast to Fort Lauderdale. Find your
            city below.
          </Typography>
          <Button
            variant="contained"
            size="large"
            startIcon={<Schedule />}
            onClick={() => {
              trackButtonClick('book_now', 'service_areas_hub');
              navigate(getBookingFlowPath());
            }}
            sx={{
              mt: 4,
              bgcolor: '#4CAF50',
              color: 'white',
              px: 4,
              py: 1.5,
              fontWeight: 800,
              borderRadius: 3,
              textTransform: 'none',
              '&:hover': { bgcolor: '#45a049', transform: 'translateY(-2px)' },
              transition: 'all 0.3s ease',
            }}
          >
            Book Now
          </Button>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ py: { xs: 5, md: 8 } }}>
        {regions.map(({ region, cities }) => (
          <Box key={region} sx={{ mb: 6 }}>
            <Typography component="h2" variant="h5" sx={{ fontWeight: 700, mb: 3, color: 'primary.main' }}>
              {region}
            </Typography>
            <Grid container spacing={2}>
              {cities.map((c) => (
                <Grid item xs={12} sm={6} md={4} key={c.slug}>
                  <Card variant="outlined" sx={{ height: '100%' }}>
                    <CardActionArea component={RouterLink} to={`/house-cleaning/${c.slug}`} sx={{ height: '100%' }}>
                      <CardContent>
                        <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1, fontWeight: 600 }}>
                          <LocationOn fontSize="small" color="primary" />
                          {c.city}
                        </Typography>
                        <Chip label={c.county} size="small" variant="outlined" sx={{ mt: 1 }} />
                      </CardContent>
                    </CardActionArea>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Box>
        ))}
      </Container>
    </PageLayout>
  );
};

export default ServiceAreasHub;
